import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dumbbell,
  Plus,
  Calendar,
  X,
  List,
  Clock,
  Flame,
  Play,
  Square
} from "lucide-react";
import api from "services/api";
import useStore from "services/store";
import toast from "react-hot-toast";

export function ExerciseDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("exercisePlan");
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [exercisePlan, setExercisePlan] = useState(null);
  const [preferences, setPreferences] = useState(null);

  useEffect(() => {
    getExercisePlan();
  }, []);

  const getExercisePlan = async () => {
    try {
      const response = await api.get("/exercise-plans/");
      if (!response.ok) {
        toast.error(response.message);
        return;
      }
      setExercisePlan(response.data.plan);
      setPreferences(response.data.preferences);
    } catch (error) {
      toast.error("Failed to load exercise plan");
    }
  };

  const handleExerciseSelect = (exercise) => {
    setSelectedExercise(exercise);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-black mb-2">
            Exercise Dashboard
          </h1>
          <p className="text-gray-600">Track your workouts and exercise plans</p>
        </div>

        {/* Exercise Preferences Card */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-6">
          <h2 className="text-lg font-semibold text-black mb-4 flex items-center">
            <Dumbbell className="mr-2 text-blue-500" size={20} />
            Your Exercise Preferences
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <PreferenceCard
              title="Workout Type"
              value={preferences?.type || 'N/A'}
              icon={<Dumbbell size={20} />}
              color="text-red-500"
            />
            <PreferenceCard
              title="Sessions/week"
              value={preferences?.sessions_per_week || 'N/A'}
              icon={<Calendar size={20} />}
              color="text-orange-500"
            />
            <PreferenceCard
              title="Duration (min)"
              value={preferences?.session_duration || 'N/A'}
              icon={<Clock size={20} />}
              color="text-yellow-500"
            />
            <PreferenceCard
              title="Equipment"
              value={preferences?.equipment?.length || 'N/A'}
              icon={<List size={20} />}
              color="text-purple-500"
            />
          </div>
          {/* Equipment List */}
          {preferences?.equipment && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Equipment:</h3>
              <div className="flex flex-wrap gap-2">
                {preferences.equipment.map((item, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab("exercisePlan")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "exercisePlan"
                    ? "border-gray-500 text-gray-500"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Calendar className="inline mr-2" />
                Exercise Plan
              </button>
              <button
                onClick={() => setActiveTab("logExercise")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "logExercise"
                    ? "border-gray-500 text-gray-500"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Plus className="inline mr-2" />
                Exercise Log
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "exercisePlan" ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Your Weekly Exercise Plan
                  </h3>
                  <button className="text-gray-600 hover:text-gray-700 text-sm font-medium">
                    View Full Plan
                  </button>
                </div>
                <div className="space-y-4">
                  {exercisePlan ? (
                    exercisePlan.map((dayPlan) => (
                      <ExercisePlanDay
                        key={dayPlan._id.$oid}
                        day={dayPlan.day}
                        warmup={dayPlan.warmup}
                        main_workout={dayPlan.main_workout}
                        cooldown={dayPlan.cooldown}
                        onExerciseSelect={handleExerciseSelect}
                      />
                    ))
                  ) : (
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 text-center">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">No Exercise Plan Found</h3>
                      <p className="text-gray-600 mb-6">Create a personalized exercise plan to get started</p>
                      <button
                        onClick={() => navigate("/create-exercise-plan")}
                        className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition-colors"
                      >
                        Create Exercise Plan
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <ComingSoonCard />
            )}
          </div>
        </div>
      </div>

      {/* Exercise Modal */}
      <ExerciseModal
        exercise={selectedExercise}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedExercise(null);
        }}
      />
    </div>
  );
}

const PreferenceCard = ({ title, value, icon, color }) => (
  <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
    <div className="flex items-center justify-between mb-2">
      <div className={`text-2xl ${color}`}>{icon}</div>
      <div className="text-right">
        <div className="text-lg font-bold text-black">{typeof value === 'string' ? value.charAt(0).toUpperCase() + value.slice(1) : value}</div>
      </div>
    </div>
    <div className="text-xs text-gray-500">{title}</div>
  </div>
);

const ComingSoonCard = () => (
  <div className="bg-white rounded-lg p-8 text-center border border-gray-200 shadow-sm">
    <div className="text-black text-4xl mb-4">
      <Plus className="mx-auto" size={48} />
    </div>
    <h3 className="text-xl font-semibold text-black mb-2">Coming Soon!</h3>
    <p className="text-black mb-4">
      We're working hard to bring you the exercise log feature. Stay tuned for updates!
    </p>
    <div className="inline-flex items-center px-4 py-2 bg-gray-500 text-white rounded-full text-sm font-medium">
      <Calendar className="mr-2" size={16} />
      In Development
    </div>
  </div>
);

const ExercisePlanDay = ({ day, warmup, main_workout, cooldown, onExerciseSelect }) => {

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 mb-4">
      <h3 className="text-lg font-semibold text-black mb-3 flex items-center">
        <Calendar className="mr-2 text-blue-500" size={20} />
        Day {day}
      </h3>

      {/* Warmup Section */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-black mb-2 flex items-center">
          <Play className="mr-1 text-orange-500" size={16} />
          Warmup
        </h4>
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <ul className="space-y-1 text-sm text-gray-600">
            {warmup.map((exercise, index) => (
              <li key={index} className="flex items-start">
                <span className="text-orange-500 mr-2">•</span>
                {exercise}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Main Workout Section */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-black mb-2 flex items-center">
          <Flame className="mr-1 text-red-500" size={16} />
          Main Workout
        </h4>
        <div className="space-y-2">
          {main_workout.map((exercise) => (
            <div
              key={exercise._id.$oid}
              className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Dumbbell className="text-red-500 mr-2" size={16} />
                  <button
                    onClick={() => onExerciseSelect(exercise)}
                    className="font-medium text-black hover:text-gray-600 transition-colors cursor-pointer text-left"
                  >
                    {exercise.name}
                  </button>
                </div>
                <div className="text-sm text-gray-600">
                  {exercise.type}
                </div>
              </div>
              <div className="flex gap-4 mt-2 text-xs text-gray-500">
                <span>{exercise.sets} sets</span>
                {exercise.reps > 0 && <span>{exercise.reps} reps</span>}
                {exercise.duration > 0 && <span>{exercise.duration}s</span>}
                <span>{exercise.rest}s rest</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cooldown Section */}
      <div>
        <h4 className="text-sm font-medium text-black mb-2 flex items-center">
          <Square className="mr-1 text-blue-500" size={16} />
          Cooldown
        </h4>
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <ul className="space-y-1 text-sm text-gray-600">
            {cooldown.map((exercise, index) => (
              <li key={index} className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                {exercise}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const ExerciseModal = ({ exercise, isOpen, onClose }) => {
  if (!isOpen || !exercise) return null;

  const getTypeColor = (type) => {
    switch (type) {
      case "lifting":
        return "text-red-500";
      case "core":
        return "text-green-500";
      case "cardio":
        return "text-blue-500";
      case "mobility":
        return "text-yellow-500";
      case "bodyweight":
        return "text-purple-500";
      default:
        return "text-gray-500";
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "lifting":
        return <Dumbbell />;
      case "core":
        return <Flame />;
      case "cardio":
        return <Play />;
      case "mobility":
        return <Clock />;
      case "bodyweight":
        return <Dumbbell />;
      default:
        return <Dumbbell />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className={`mr-3 ${getTypeColor(exercise.type)}`}>
              {getTypeIcon(exercise.type)}
            </div>
            <h2 className="text-xl font-bold text-black">{exercise.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Exercise Details */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-black mb-3 flex items-center">
              <Dumbbell className="mr-2 text-blue-500" size={20} />
              Exercise Details
            </h3>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <PreferenceCard
                  title="Sets"
                  value={exercise.sets}
                  icon={<Dumbbell size={20} />}
                  color="text-red-500"
                />
                {exercise.reps > 0 ? (
                  <PreferenceCard
                    title="Reps"
                    value={exercise.reps}
                    icon={<Flame size={20} />}
                    color="text-orange-500"
                  />
                ) : (
                  <PreferenceCard
                    title="Duration"
                    value={`${exercise.duration}s`}
                    icon={<Clock size={20} />}
                    color="text-yellow-500"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Rest Time */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-black mb-3 flex items-center">
              <Clock className="mr-2 text-blue-500" size={20} />
              Rest Time
            </h3>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-purple-500">
                  <Clock size={20} />
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-black">{exercise.rest}s</div>
                  <div className="text-xs text-gray-500">Rest between sets</div>
                </div>
              </div>
            </div>
          </div>

          {/* Exercise Type */}
          <div>
            <h3 className="text-lg font-semibold text-black mb-3 flex items-center">
              <List className="mr-2 text-blue-500" size={20} />
              Exercise Type
            </h3>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div className={getTypeColor(exercise.type)}>
                  {getTypeIcon(exercise.type)}
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-black capitalize">{exercise.type}</div>
                  <div className="text-xs text-gray-500">Type</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};